import React, {FunctionComponent} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {Fabric} from "office-ui-fabric-react/lib/Fabric";
import {
	DetailsList,
	DetailsListLayoutMode,
	IColumn,
	Selection,
	SelectionMode,
} from "office-ui-fabric-react/lib/DetailsList";
import {MarqueeSelection} from "office-ui-fabric-react/lib/MarqueeSelection";

// Config
import {scopes} from "./auth-config";

// Microsoft Icons
import {initializeIcons} from "@uifabric/icons";

// Components
import {Breadcrumbs} from "./components/Breadcrumbs/Breadcrumbs";
import {FluentCommandBar} from "./components/CommandBar/FluentCommandBar";

// Styles
import {classNames} from "./DetailsListDocuments.styles";

// Interfaces
import {DetailsListDocumentsExampleState, DisplayDocument, Document,} from "./types/types";

// Helper methods
import {formatDocuments} from "./helpers/helpers";
import {IBreadcrumbItem, Link, SearchBox} from "@fluentui/react";

// const selection: Selection = null;
import {getFilesInFolder, getGroupDriveFiles, setParentId, setSelectedDocuments,} from "./store/actions/dataActions";

const _getKey = (item: any): string => {
	return item.key;
};

function _copyAndSort<T>(
	items: T[],
	columnKey: string,
	isSortedDescending?: boolean
): T[] {
	const key = columnKey as keyof T;
	return items
		.slice(0)
		.sort((a: T, b: T) =>
			(isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1
		);
}

export const DetailsListDocuments: FunctionComponent = () => {
	// State
	//const [selection, setSelection] = React.useState<Selection>();
	const [breadcrumbs, setBreadcrumbs] = React.useState<IBreadcrumbItem[]>([]);
	const [allItems, setAllItems] = React.useState<DisplayDocument[]>([]);
	const [state, setState] = React.useState<DetailsListDocumentsExampleState>();
	const [selectionDetails, setSelectionDetails] = React.useState<string>(
		"No items selected"
	);
	const [columns, setColumns] = React.useState<IColumn[]>([]);

	const dispatch = useDispatch();
	const {t, i18n} = useTranslation();

	let selection = new Selection({
		onSelectionChanged: () => {
			setSelectionDetails(_getSelectionDetails());
		},
	});

	const documents = useSelector((state: any) => state.data.documents);

	React.useEffect(() => {
		// Columns
		let initialColumns = [
			{
				key: "column1",
				name: "File Type",
				className: classNames.fileIconCell,
				iconClassName: classNames.fileIconHeaderIcon,
				ariaLabel:
					"Column operations for File type, Press to sort on File type",
				iconName: "Page",
				isIconOnly: true,
				fieldName: "fileType",
				minWidth: 16,
				maxWidth: 16,

				onRender: (item: DisplayDocument) => {
					return (
						<img
							src={item.iconName}
							className={classNames.fileIconImg}
							alt={item.fileType + " file icon"}
						/>
					);
				},
			},
			{
				key: "column2",
				name: t("name"),
				fieldName: "name",
				minWidth: 210,
				maxWidth: 350,
				isRowHeader: true,
				isResizable: true,
				isSorted: true,
				isSortedDescending: false,
				sortAscendingAriaLabel: "Sorted A to Z",
				sortDescendingAriaLabel: "Sorted Z to A",
				data: "string",
				isPadded: true,
			},
			{
				key: "column3",
				name: t("dateModified"),
				fieldName: "dateModifiedValue",
				minWidth: 70,
				maxWidth: 90,
				isResizable: true,
				data: "number",
				onRender: (item: DisplayDocument) => {
					return <span>{item.dateModified}</span>;
				},
				isPadded: true,
			},
			{
				key: "column4",
				name: t("modifiedBy"),
				fieldName: "modifiedBy",
				minWidth: 70,
				maxWidth: 150,
				isResizable: true,
				isCollapsible: true,
				data: "string",
				onRender: (item: DisplayDocument) => {
					return <span>{item.modifiedBy}</span>;
				},
				isPadded: true,
			},
			{
				key: "column5",
				name: t("fileSize"),
				fieldName: "fileSizeRaw",
				minWidth: 70,
				maxWidth: 90,
				isResizable: true,
				isCollapsible: true,
				data: "number",
				onRender: (item: DisplayDocument) => {
					return <span>{item.fileSize}</span>;
				},
			},
		];

		setColumns(initialColumns);

		setState({
			...state,
			columns: initialColumns,
		});
	}, [t, i18n]);

	React.useEffect(() => {
		let steps: IBreadcrumbItem[] = [
			// { text: "Files", key: "Files", onClick: () => console.log("Clicked") },
			// { text: "Folder 1", key: "d1", onClick: () => console.log("Clicked") },
			// Generally, only the last item should ever be a heading.
			// It would typically be h1 or h2, but we're using h4 here to better fit the structure of the page.
			{text: t("files"), key: "Files", isCurrentItem: true, as: "h4"},
		];

		setBreadcrumbs(steps);
	}, [t]);

	React.useEffect(() => {
		initializeIcons();

		let formattedDocuments = formatDocuments(documents);

		setAllItems(formattedDocuments);
	}, [documents]);

	React.useEffect(() => {
		setState({
			items: allItems,
			columns: columns,
			isModalSelection: true,
			isCompactMode: false,
			announcedMessage: undefined,
		});
		setSelectionDetails(_getSelectionDetails());
	}, [allItems]);

	const breadCrumbChange = (item: any) => {
		if (breadcrumbs[breadcrumbs.length - 1].key !== item.key) {
			// Update file view with files in selected item
			dispatch(getFilesInFolder(scopes, item.key));
			dispatch(setParentId(item.key));

			// Check if there are more than one breadcrumb
			let newSteps: IBreadcrumbItem[] = [];

			breadcrumbs.forEach((breadcrumb: IBreadcrumbItem) => {
				newSteps.push({
					text: breadcrumb.text,
					key: breadcrumb.key,
					onClick: () => breadcrumbClick(breadcrumb),
				});
			});
			// Newest item
			newSteps.push({
				text: item.name,
				key: item.key,
				isCurrentItem: true,
				as: "h4",
			});
			setBreadcrumbs(newSteps);
		}
	};

	const breadcrumbClick = (breadcrumb: IBreadcrumbItem) => {
		if (breadcrumb.key === "Files") {
			dispatch(getGroupDriveFiles(scopes));
		} else {
			dispatch(getFilesInFolder(scopes, breadcrumb.key));
			dispatch(setParentId(breadcrumb.key));
		}

		let newSteps: IBreadcrumbItem[] = [];
		for (let i = 0; i < breadcrumbs.indexOf(breadcrumb); i++) {
			newSteps.push({
				text: breadcrumbs[i].text,
				key: breadcrumbs[i].key,
				onClick: () => breadcrumbClick(breadcrumb),
			});
		}
		// Newest item
		newSteps.push({
			text: breadcrumb.text,
			key: breadcrumb.key,
			isCurrentItem: true,
			as: "h4",
		});
		setBreadcrumbs(newSteps);
	};

	function _renderItemColumn(
		item: DisplayDocument,
		index: number,
		column: IColumn
	) {
		const fieldContent = item[
			column.fieldName as keyof DisplayDocument
			] as string;

		switch (column.key) {
			case "column2":
				if (item.fileType !== "folder") {
					return (
						<Link href={item.webUrl} target="_blank">
							<span className={classNames.link}>{fieldContent}</span>
						</Link>
					);
				} else {
					return (
						<Link onClick={() => breadCrumbChange(item)}>
							<span className={classNames.link}>{fieldContent}</span>
						</Link>
					);
				}

			default:
				return <span>{fieldContent}</span>;
		}
	}

	const _onChangeText = (
		_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
		text: string
	): void => {
		setState({
			...state,
			items: text
				? allItems.filter((i: any) => i.name.toLowerCase().indexOf(text) > -1)
				: allItems,
		});
	};

	const _onItemInvoked = (item: any): void => {
		if (item.fileType === "folder") {
			breadCrumbChange(item);
		} else {
			window.open(item.webUrl, "_blank");
		}
	};

	const _getSelectionDetails = (): string => {
		const selectionCount = selection.getSelectedCount();

		// Set selected items to state
		dispatch(setSelectedDocuments(selection.getSelection()));

		switch (selectionCount) {
			case 0:
				return "No items selected";
			case 1:
				return (
					"1 item selected: " + (selection.getSelection()[0] as Document).name
				);
			default:
				return `${selectionCount} items selected`;
		}
	};

	const _onColumnClick = (
		ev: React.MouseEvent<HTMLElement>,
		column: IColumn
	): void => {
		const {columns, items} = state;
		const newColumns: IColumn[] = columns.slice();
		const currColumn: IColumn = newColumns.filter(
			(currCol) => column.key === currCol.key
		)[0];

		newColumns.forEach((newCol: IColumn) => {
			if (newCol === currColumn) {
				currColumn.isSortedDescending = !currColumn.isSortedDescending;
				currColumn.isSorted = true;

				setState({
					...state,
					announcedMessage: `${currColumn.name} is sorted ${
						currColumn.isSortedDescending ? "descending" : "ascending"
					}`,
				});
			} else {
				newCol.isSorted = false;
				newCol.isSortedDescending = true;
			}
		});

		const newItems = _copyAndSort(
			items,
			currColumn.fieldName,
			currColumn.isSortedDescending
		);
		setState({...state, columns: newColumns, items: newItems});
	};

	return selection && state ? (
		<Fabric>
			{/* CommandBar */}
			<FluentCommandBar/>

			{/* Breadcrumbs */}
			<div className={classNames.subHeaderWrapper}>
				<div className={classNames.breadcrumbWrapper}>
					<Breadcrumbs steps={breadcrumbs}/>
				</div>
				<div className={classNames.searchInputWrapper}>
					<SearchBox placeholder={t("search")} onChange={_onChangeText}/>
				</div>
			</div>

			<div style={{paddingTop: 0}}>
				<MarqueeSelection selection={selection}>
					<DetailsList
						onColumnHeaderClick={_onColumnClick}
						items={state.items}
						compact={state.isCompactMode}
						columns={state.columns}
						selectionMode={SelectionMode.multiple}
						getKey={_getKey}
						setKey="multiple"
						layoutMode={DetailsListLayoutMode.justified}
						isHeaderVisible={true}
						selection={selection}
						selectionPreservedOnEmptyClick={true}
						onRenderItemColumn={_renderItemColumn}
						onItemInvoked={_onItemInvoked}
						enterModalSelectionOnTouch={true}
						ariaLabelForSelectionColumn="Toggle selection"
						ariaLabelForSelectAllCheckbox="Toggle selection for all items"
						checkButtonAriaLabel="Row checkbox"
					/>
				</MarqueeSelection>
			</div>
		</Fabric>
	) : null;
};
